import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {LogOut} from "../domains/common/components/Head";
import {
  CircularProgress, Grid, LinearProgress,
  Tooltip,
  useTheme
} from "@mui/material";
import Box from "@mui/material/Box";
import {
  IndentSpace,
  Link1,
  PlainText,
  SecondaryTextSmall,
  Title4
} from "../domains/common/components/Typography";
import {OkIcon, WarningIcon} from "../domains/common/components/SvgIcons";
import {ErrorSignChip, OnSignChip, SignedChip} from "../domains/common/components/Chips";
import {Notify} from "../domains/common/components/AlertNotify";
import {exceptionsTitle, signProviderTitle} from "../dictionaries";
import {
  checkSigningStatus,
  getParticipantsDocsList, sendDocToOrganizationSign,
  sendDocToPersonSign, sendRejectExtSign,
  sendRejectSign, sendToInternalSign, sendToSignGuarantor
} from "../domains/sign/index";
import {Item} from "../domains/common/components/Item";
import {RolesEnums, SubjectType} from "../enums";
import {DealHasChangedAfterStatementComponent} from "../domains/common/components/CriticalErrors";
import {BackButtonToDeal, ButtonBase, ButtonGroupCustom} from "../domains/common/components/Button";

import {hasStatus} from "./deal/status";

const queryParameters = new URLSearchParams(window.location.search)
const urlQueryDealId = queryParameters.get("dealId")

const SignPageContext = createContext();

export default function SignPage() {
  /** @type {[SigningData | null, function]} */
  const [list, setList] = useState([])
  const [signConfigs, setSignConfigs] = useState({})
  const [noDocs, setNoDocs] = useState(false)
  const [notify, openNotify] = Notify({open: false});
  const [showProgress, setShowProgressState] = useState(false);
  const [disableForSign, setDisableForSign] = useState(false);

  let progressLine;

  const theme = useTheme();

  const getListParticipantsWDocs = async () => {
    const result = await getParticipantsDocsList(urlQueryDealId)

    setDisableForSign(result?.isBlockedForRegistration)

    setSignConfigs(result.configs)

    if (Array.isArray(result.participantsDocuments))
      setList(result)
    else
      setNoDocs(true)
  }

  const sendToSign = async (participantId, participantType, provider) => {
    setShowProgressState(true)

    if (!participantType)
      openNotify({msg: "Неизвестный тип участника", duration: 12000, type: 'error'})

    provider ? !provider : 'signme';

    const result = await sendDocToPersonSign(urlQueryDealId, participantId, provider);

    if (result?.ok) {
      await getListParticipantsWDocs();
      openNotify({msg: "Документы отправлены на подпись", duration: 3000})
    } else {
      const msg = exceptionsTitle[result.message] || result.message || "Не удалось отправить документы на подпись";
      openNotify({msg, duration: 12000, errors: result.errors, type: 'error'})
    }

    setShowProgressState(false);
  }

  const handleSignGuarantor = async (intSignerId, participantId) => {
    setShowProgressState(true)

    const result = await sendToSignGuarantor(urlQueryDealId, participantId, intSignerId);

    if (result?.ok) {
      await getListParticipantsWDocs();
      openNotify({msg: "Документы отправлены на подпись представителю", duration: 3000})
    } else {
      const msg = exceptionsTitle[result.message] || result.message || "Не удалось отправить документы на подпись представителю";
      openNotify({msg, duration: 12000, errors: result.errors, type: 'error'})
    }

    setShowProgressState(false);
  }

  const rejectDocs = async (participantId, provider) => {
    setShowProgressState(true)
    const result = await sendRejectSign(urlQueryDealId, participantId, provider);

    if (result.ok) {
      await getListParticipantsWDocs();
      openNotify({msg: "Документы отозваны с подписания", duration: 3000})
    } else {
      const msg = exceptionsTitle[result.message] || "Не удалось отозвать документы с подписания";
      openNotify({msg, duration: 12000, errors: result.errors, type: 'error'})
    }
    setShowProgressState(false);
  }

  const checkStatus = async (participantId, provider) => {
    setShowProgressState(true)
    const result = await checkSigningStatus(urlQueryDealId, participantId, provider);

    const map = {
      notSigned: 'Документы не подписаны',
      signed: 'Документы подписаны',
      rejected: 'Участник отозвал документы с подписи',
      deleted: 'Документы удалены',
      partialSigned: 'Документы подписаны частично'
    }

    if (result.ok && result.data?.status) {
      if (['rejected', 'signed'].includes(result.data?.status)) {
        await getListParticipantsWDocs();
      }

      openNotify({msg: map[result.data.status], duration: 8000})
    } else {
      const msg = exceptionsTitle[result.message] || "Во время запроса произошла ошибка";
      openNotify({msg, duration: 12000, type: 'error'})
    }
    setShowProgressState(false);
  }

  if (showProgress) {
    progressLine = <LinearProgress/>
  }

  useEffect(() => {
    getListParticipantsWDocs();
  }, []);

  const onSendToInternalSign = async () => {
    setTimeout(() => getListParticipantsWDocs(), 7000);
  }

  const contextValue = {
    openNotify,
    disableForSign,
    setDisableForSign,
    sendToSign,
    showProgress,
    setShowProgressState,
    getListParticipantsWDocs,
    checkStatus,
    rejectDocs,
    handleSignGuarantor,
    signConfigs
  };

  return (
    <SignPageContext.Provider value={contextValue}>
      <Box sx={{
        width: "100%", height: '100%', pb: '2px', backgroundColor: theme.palette.background.secondary
      }}>
        <div style={{height: '5px'}}>
          {progressLine}
        </div>
        {notify}
        <Grid container spacing={0} wrap="nowrap" sx={{overflow: "auto"}}>
          <Grid item xs={3} sx={{backgroundColor: theme.palette.background.secondary, minWidth: '80px'}}>
            <BackButtonToDeal dealId={urlQueryDealId}/>
          </Grid>
          <Grid item xs={6} sx={{minWidth: '900px', backgroundColor: theme.palette.background.default}}>
            <Item>
              <Title4 title="Подписание документов сделки"/>
              <Grid container spacing={1}>
                <Grid item xs={12} sx={{height: 20}}/>
                <Grid item xs={12}>
                  {noDocs ? <NoDocToSign/> : void 0}
                  {
                    disableForSign ?
                      <DealHasChangedAfterStatementComponent/> : void 0
                  }
                  <Grid item xs={12} sx={{height: 20}}/>
                  {/*ФИЗИЧЕСКИЕ лица */}
                  {list && list?.participantsDocuments?.map((participantDocument) => {
                    if (participantDocument.type === SubjectType.ORGANIZATION || participantDocument.guarantor)
                      return;

                    // текущий провайдер подписания если уже отправили на подпись
                    const signProvider = getSignProviderForDocuments(participantDocument);

                    return (<Grid container spacing={0} alignItems="left" justifyContent="left" sx={{ml: 5}}>
                      <Grid item xs={12} sx={{fontSize: 16}}>
                        {participantDocument.fio}
                        <RepresentiveDesc representive={participantDocument.representive}/>
                        <IndentSpace size={10}/>
                        <SignDesc signature={participantDocument.signature}/>
                      </Grid>
                      <Grid item xs={12} sx={{height: 10}}/>
                      <Grid item xs={11} sx={{ml: 1}}>
                        <DocumentsListToSign listItem={participantDocument}/>
                        <XMLStatementsListToSign listItem={participantDocument}/>
                      </Grid>
                      <Grid item xs={12} sx={{height: 20}}/>
                      <Grid item xs={12}>
                        {signProvider ?
                          <SecondaryTextSmall variant="caption" gutterBottom>Документы отправлены в
                            сервис {signProviderTitle[signProvider]}</SecondaryTextSmall> : void 0}
                      </Grid>
                      <Grid item xs={11} style={{textAlign: "right"}}>
                        <PersonSignButtonsComponent participantDocument={participantDocument}/>
                      </Grid>
                      <Grid item xs={12} sx={{height: 50}}/>
                    </Grid>)
                  })}
                  {/*ЮРИДИЧЕСКИЕ лица */}
                  {list && list?.participantsDocuments?.map((participantDocument) => {
                    if (participantDocument.type === SubjectType.PERSON || participantDocument.guarantor)
                      return;
                    return (<Grid container spacing={0} alignItems="left" justifyContent="left" sx={{ml: 5}}>
                      <Grid item xs={12} sx={{fontSize: 16}}>
                        {participantDocument.fio}
                        <RepresentiveDesc representive={participantDocument.representive}/>
                        <IndentSpace size={10}/>
                        <SignDesc signature={participantDocument.signature}/>
                      </Grid>
                      <Grid item xs={12} sx={{height: 10}}/>
                      <Grid item xs={11} sx={{ml: 1}}>
                        <DocumentsListToSign listItem={participantDocument} disabled={true}/>
                        <XMLStatementsListToSign listItem={participantDocument} disabled={true}/>
                      </Grid>
                      <Grid item xs={12} sx={{height: 20}}/>
                      <Grid item xs={12}>
                        {participantDocument.externalSignLink ?
                          <SecondaryTextSmall variant="caption" gutterBottom>Ссылка для внешнего подписания
                            создана</SecondaryTextSmall> : void 0}
                      </Grid>
                      <Grid item xs={11} style={{textAlign: "right"}}>
                        <OrganizationSignButtonsComponent participantDocument={participantDocument}/>
                      </Grid>
                      <Grid item xs={12} sx={{height: 50}}/>
                    </Grid>)
                  })}
                  {/*ЮРИДИЧЕСКИЕ лица ГАРАНТЫ */}
                  <GuarantorsDocumentList participantsDocuments={list?.participantsDocuments}
                                          internalSigners={list.internalSigners}/>
                  {
                    list.internalSigners?.map((internalSigners) => {
                      if (internalSigners?.documents?.length)
                        return (
                          <Grid container spacing={0} alignItems="left" justifyContent="left" sx={{ml: 5}}>
                            <Grid item xs={8} sx={{fontSize: 16}}>
                              {internalSigners.name}
                              <IndentSpace size={10}/>
                              <SecondaryTextSmall variant="caption" gutterBottom>Внутренний
                                подписант</SecondaryTextSmall>
                            </Grid>
                            <Grid item xs={12} sx={{height: 10}}/>
                            <Grid item xs={11} sx={{ml: 1}}>
                              <InternalSignDocuments documents={internalSigners?.documents}
                                                     onSendToSign={onSendToInternalSign}/>
                            </Grid>
                          </Grid>
                        )
                    })
                  }
                </Grid>
              </Grid>
            </Item>
          </Grid>
          <Grid item xs={3} sx={{backgroundColor: theme.palette.background.secondary, minWidth: '80px'}}>
            <Box sx={{lineHeight: '50px'}} display="flex" justifyContent="flex-end"><LogOut/></Box>
          </Grid>
        </Grid>
      </Box>
    </SignPageContext.Provider>)
}


const GuarantorsDocumentList = ({participantsDocuments, internalSigners}) => {
  const {
    disableForSign,
    showProgress,
    handleSignGuarantor
  } = useContext(SignPageContext);

  let options = internalSigners?.map((item) => {
    return {key: item.id, value: item.name}
  })

  return participantsDocuments?.map((participantDocument) => {
    if (participantDocument.type === SubjectType.PERSON || !participantDocument.guarantor)
      return;

    return (<Grid container spacing={0} alignItems="left" justifyContent="left" sx={{ml: 5}}>
      <Grid item xs={12} sx={{fontSize: 16}}>
        {participantDocument.fio}
        <IndentSpace size={10}/>
      </Grid>
      <Grid item xs={12} sx={{height: 10}}/>
      <Grid item xs={11} sx={{ml: 1}}>
        <DocumentsListToSign listItem={participantDocument} disabled={true}/>
        <XMLStatementsListToSign listItem={participantDocument} disabled={true}/>
      </Grid>
      <Grid item xs={12} sx={{height: 20}}/>
      <Grid item xs={11} style={{textAlign: "right"}}>
        <ButtonGroupCustom onMenuClick={(itemKey) => handleSignGuarantor(itemKey, participantDocument.participantId)}
                           disabled={!!showProgress || disableForSign}
                           title={"Подписать"}
                           options={options}
                           sxMenuList={{width: 310}}
                           openOnButtonClick={true}/>
      </Grid>
      <Grid item xs={12} sx={{height: 50}}/>
    </Grid>)
  })
}
export function DocumentsListToSign({listItem, disabled}) {
  const {documents, signState, participantId} = listItem;

  return documents?.map((doc) => {
    const signInfo = signState?.find(item => item.documentId === doc.id);
    const signed = !!(signInfo?.signAt);
    const sendToSignAt = signInfo?.sendToSignAt;

    return (
      <FileItem
        key={doc.id}
        doc={doc}
        signed={signed}
        sendToSignAt={sendToSignAt}
      />
    );
  }) ?? [];
}

export function XMLStatementsListToSign({listItem}) {
  const xmls = listItem.xmls;

  if (listItem.role === RolesEnums.EXT_SIGNER)
    return

  if (xmls?.length) {
    const result = []

    xmls.map((xml) => {
      let signed = false;
      if (xml.signlink) {
        signed = true;
      }
      result.push(<FileItem doc={{
        fileName: xml.name,
        fileDesc: 'XML-заявление',
        fileLink: xml.cdnLink,
        signlink: xml.signlink,
        hasSignature: true,
        isXml: true,
        id: xml.id
      }} signed={signed} sendToSignAt={xml.sendToSignAt}/>);
    })
    return result;
  } else {
    return (
      <SecondaryTextSmall variant="caption" gutterBottom><WarningIcon size="18"/> XML-заявления
        отсутствуют</SecondaryTextSmall>
    )
  }
}

/**
 * Список документов внутренних подписантов
 *
 */
export function InternalSignDocuments({documents, onSendToSign}) {
  const [showProgress, setShowProgressState] = useState(false);
  const [notify, openNotify] = Notify({open: false});
  const result = [];

  const sendToSign = async () => {
    setShowProgressState(true);
    const result = await sendToInternalSign(urlQueryDealId);

    if (result.ok) {
      openNotify({msg: "Документы отправлены на подпись", duration: 3000})

      onSendToSign ? onSendToSign() : void 0;
    } else {
      const msg = exceptionsTitle[result.message] || result.message || "Не удалось отправить документы на подпись";
      openNotify({msg, duration: 12000, errors: result.errors, type: 'error'})
    }
    setShowProgressState(false);
  }

  documents?.map((doc) => {
    const signedChip = doc.internalSignStatus === 'signed' ?
      <>
        <SignedChip/>
        <IndentSpace size={10}/>
        <Tooltip title="Скачать подпись">
          <Link1 href={doc?.signlink} target="_blank" sx={{fontWeight: 'bold'}}>.sig</Link1>
        </Tooltip>
      </> : void 0
    const signOnChip = doc.internalSignStatus === 'onSign' ? <OnSignChip/> : void 0
    const signError = doc.internalSignStatus === 'error' ? <ErrorSignChip/> : void 0

    result.push(
      <>
        {notify}
        <Grid container spacing={0} alignItems="center" justifyContent="flex">
          <Grid item xs={3} sx={{
            fontSize: 13,
            lineHeight: "100%",
          }}>{doc.fileDesc}</Grid>
          <Grid item xs={5} sx={{
            ml: 2,
            lineHeight: "42px",
          }}><Link1 href={doc.fileLink || doc.link} target="_blank">{doc.fileName}</Link1></Grid>
          <Grid item xs={3} sx={{
            lineHeight: "42px",
          }}>
            {signedChip}
            {signOnChip}
            {signError}
          </Grid>
        </Grid>
      </>
    )
  })

  result.push(
    <Grid container spacing={0} alignItems="center" justifyContent="flex">
      <Grid item xs={12} sx={{height: 20}}/>
      <Grid item xs={12} style={{textAlign: "right"}}>
        {!hasStatus('dealSentToRR') ?
          <ButtonBase variant="contained" size="small" onClick={sendToSign}
                      disabled={!!showProgress}>Подписать</ButtonBase> : void 0}
        <IndentSpace size={15}/>
        {showProgress ? <CircularProgress size="10px"/> : void 0}
      </Grid>
    </Grid>
  )

  return result;
}

function FileItem({doc, signed, sendToSignAt}) {
  const downloadUrl = !doc?.isXml ? doc?.link : doc?.fileLink || doc?.link

  const signedChip = useMemo(() =>
      signed ? (
        <>
          <SignedChip/>
          <IndentSpace size={10}/>
          <Tooltip title="Скачать подпись">
            <Link1 href={doc?.signlink} target="_blank" sx={{fontWeight: 'bold'}}>
              .sig
            </Link1>
          </Tooltip>
        </>
      ) : null
    , [signed, doc?.signlink]);

  const signOnInfoChip = !signed && sendToSignAt ? <OnSignChip/> : null

  return (
    <Grid container spacing={0} alignItems="center" justifyContent="flex">
      <Grid item xs={3} sx={{fontSize: 13, height: 35}}>{doc?.fileDesc}</Grid>
      <Grid item xs={5} sx={{ml: 2, height: 35}}>
        <Link1 href={downloadUrl} target="_blank">{doc?.fileName}</Link1>
      </Grid>
      <Grid item xs={3} sx={{height: 35}}>
        {signedChip}
        {signOnInfoChip}
      </Grid>
    </Grid>
  )
}

export function SignDesc({signature}) {
  const style = {display: "inline-block", verticalAlign: 'top'};
  if (!signature)
    return (
      <div style={style}>
        <div style={style}><WarningIcon size="18"/></div>
        <div style={style}><IndentSpace size={5}/>
          <SecondaryTextSmall variant="caption" gutterBottom>Проверка наличия ЭП не пройдена</SecondaryTextSmall>
        </div>
      </div>
    )

  if (!signature?.signme?.precheck) {
    return (
      <div style={style}>
        <div style={style}><WarningIcon size="18"/></div>
        <div style={style}>
          <IndentSpace size={5}/>
          <SecondaryTextSmall variant="caption" gutterBottom>Проверка наличия ЭП не пройдена</SecondaryTextSmall>
        </div>
      </div>
    );
  }

  if (
    signature?.signme?.precheck?.error === 'Not Found' ||
    signature?.signme?.userinfo?.error === 'Not Found'
  ) {
    return (
      <div style={style}>
        <div style={style}>
          <WarningIcon size="18"/>
        </div>
        <div style={style}>
          <IndentSpace size={5}/>
          <SecondaryTextSmall variant="caption" gutterBottom> Не зарегистрирован в SignMe</SecondaryTextSmall>
        </div>
      </div>);
  }

  return (
    <div style={style}>
      <div style={style}>
        <OkIcon size="18"/></div>
      <div style={style}><IndentSpace size={5}/>
        <SecondaryTextSmall variant="caption" gutterBottom style={{alignItems: 'center'}}> Зарегистрирован в
          SignMe</SecondaryTextSmall>
      </div>
    </div>
  );
}

export function NoDocToSign() {
  return <PlainText variant="caption" gutterBottom><WarningIcon size="18"/> Отсутствуют документы для
    подписания</PlainText>
}

function RepresentiveDesc({representive}) {
  if (representive?.lastName)
    return (
      <>
        <SecondaryTextSmall variant="caption" gutterBottom>
          {` (в лице ${representive.lastName} ${representive?.firstName[0]}.)`}
        </SecondaryTextSmall>
      </>
    )
}

/**
 * Получить каким сервисом были подписаны доки
 * @param participantDocuments документы заявителя
 */
function getSignProviderForDocuments(participantDocuments) {
  if (participantDocuments.provider)
    return participantDocuments.provider;

  if (!participantDocuments?.signState?.length)
    return null;

  if (participantDocuments?.signState[0]?.provider)
    return participantDocuments?.signState[0]?.provider
}

const hasAllDocsSignatures = (participantDocument) => {
  // Проверяем все записи в signState
  const allSignStateSigned = participantDocument?.signState.every(sign =>
    sign.signAt !== null && sign.signAt !== undefined
  );

  // Проверяем все XML документы
  const allXmlsSigned = participantDocument?.xmls.every(xml =>
    xml.signAt !== null && xml.signAt !== undefined
  );

  return allSignStateSigned && allXmlsSigned;
}

/**
 * Кнопка отправить на подпись физ.лицу
 * @param onclick
 * @param noDocs
 * @returns {React.JSX.Element|string}
 * @constructor
 */
function SignButtonForPerson({onclick, showProgress, disabled}) {
  if (hasStatus('dealSentToRR'))
    return '';

  return (
    <ButtonBase variant="contained" size="small" onClick={() => onclick ? onclick() : null}
                disabled={!!showProgress || disabled}>Подписать</ButtonBase>
  )
}

function SignProviderButton({onclick, title, sx}) {
  const {
    disableForSign,
    showProgress,
    signConfigs
  } = useContext(SignPageContext);

  const options = []

  if(!signConfigs?.isMyDSSDisabled)
    options.push({key: 'mydss', value: 'MyDSS'})

  if(!signConfigs?.isSignMeDisabled)
    options.push({key: 'signme', value: 'SignMe'})

  if(!signConfigs?.isGosKeyDisabled)
    options.push({key: 'goskey', value: 'Госключ'})

  const handleMenuItemClick = (type) => {
    onclick ? onclick(type) : null
  };

  return (
    <ButtonGroupCustom onMenuClick={handleMenuItemClick}
                       disabled={!!showProgress || disableForSign}
                       title={title || "Подписать"}
                       options={options}
                       sxMenuList={{width: 141, ...sx}}
                       openOnButtonClick={true}/>
  );
}

/**
 * Кнопка подписания документов участником
 * @param participantDocument
 * @return {Element}
 * @constructor
 */
const PersonSignButtonsComponent = ({participantDocument}) => {
  const {
    disableForSign,
    showProgress,
    sendToSign,
    checkStatus,
    setShowProgressState,
    getListParticipantsWDocs,
    rejectDocs,
    openNotify
  } = useContext(SignPageContext);

  const {
    participantId,
    type,
    externalSignLink,
    signProcessId
  } = participantDocument;

  // Определяем каким провайдером подписаны документы участника
  const signProvider = getSignProviderForDocuments(participantDocument);

  const hasExternalLink = Boolean(externalSignLink);
  const hasSignProcess = Boolean(signProcessId);
  const canSignInside = signProvider && !hasExternalLink;
  const canChooseProvider = !signProvider && !hasExternalLink;

  const sendToExtSign = useCallback(async () => {
    setShowProgressState(true);

    try {
      const result = await sendDocToOrganizationSign(urlQueryDealId, participantId);

      if (result?.ok) {
        await getListParticipantsWDocs();
        openNotify({msg: "Документы отправлены на подпись", duration: 3000});
      } else {
        const msg =
          exceptionsTitle[result.message] ||
          result.message ||
          "Не удалось отправить документы на подпись";

        openNotify({
          msg,
          duration: 12000,
          errors: result.errors,
          type: "error"
        });
      }
    } finally {
      setShowProgressState(false);
    }
  }, [participantId, setShowProgressState, getListParticipantsWDocs, openNotify]);

  const handleSendToSign = useCallback(
    (provider) => {
      sendToSign(participantId, type, provider);
    },
    [sendToSign, participantId, type]
  );

  const renderSignButton = () => {
    if (canSignInside) {
      return (
        <SignButtonForPerson
          showProgress={showProgress}
          disabled={disableForSign}
          onclick={() => handleSendToSign(signProvider)}
        />
      );
    }

    if (canChooseProvider) {
      return (
        <SignProviderButton
          showProgress={showProgress}
          disabled={disableForSign}
          onclick={handleSendToSign}
        />
      );
    }

    return null;
  };

  const renderExternalButtons = () => {
    if (!hasExternalLink) return null;

    return (
      <>
        <RejectExtSignButton participantId={participantId}/>
        <IndentSpace size={10}/>
        <CopyButton signlink={externalSignLink}/>
      </>
    );
  };

  return (
    <>
      {hasSignProcess && (
        <>
          <ButtonBase
            variant="contained"
            size="small"
            onClick={() => rejectDocs(participantId, signProvider)}
            disabled={showProgress}
            color="error"
          >
            Отозвать
          </ButtonBase>

          <IndentSpace size={10}/>

          <ButtonBase
            variant="contained"
            size="small"
            onClick={() => checkStatus(participantId, signProvider)}
            disabled={showProgress}
          >
            Обновить статус
          </ButtonBase>
        </>
      )}

      {!hasSignProcess && !hasExternalLink && (
        <>
          {renderSignButton()}
          <IndentSpace size={10}/>

          <ButtonBase
            variant="contained"
            size="small"
            onClick={sendToExtSign}
            disabled={showProgress}
          >
            Создать ссылку
          </ButtonBase>
        </>
      )}

      {renderExternalButtons()}
    </>
  );
};

/**
 * Кнопки подписания и др. для Юридических лиц
 * @param {Object} props - Свойства компонента
 * @param {ParticipantDocumentsForSign} props.participantDocument - Массив участников-организаций для подписания
 */
const OrganizationSignButtonsComponent = ({participantDocument}) => {
  const {
    openNotify,
    disableForSign,
    showProgress,
    sendToSign,
    setShowProgressState,
    getListParticipantsWDocs,
    checkStatus,
    rejectDocs
  } = useContext(SignPageContext);

  let orgButtons;
  let personButtons;

  const {participantId} = participantDocument;

  const hasAllDocsSigned = hasAllDocsSignatures(participantDocument)

  const sendToOrgSign = async () => {
    setShowProgressState(true)

    const result = await sendDocToOrganizationSign(urlQueryDealId, participantId);

    if (result?.ok) {
      await getListParticipantsWDocs();
      openNotify({msg: "Документы отправлены на подпись", duration: 3000})
    } else {
      const msg = exceptionsTitle[result.message] || result.message || "Не удалось отправить документы на подпись";
      openNotify({msg, duration: 12000, errors: result.errors, type: 'error'})
    }

    setShowProgressState(false);
  }

  // ЮРИК
  if (participantDocument.externalSignLink) {
    orgButtons = (
      <>
        <RejectExtSignButton participantId={participantDocument.participantId}/>
        <IndentSpace size={10}/>
        <CopyButton signlink={participantDocument.externalSignLink}/>
      </>)
  }

  // ЮРИК
  if (!participantDocument.externalSignLink && !participantDocument.signProcessId)
    orgButtons = (<CopyButton signlink={participantDocument.externalSignLink}/>)

  // ФИЗИК
  if (participantDocument.representive && !participantDocument.externalSignLink) {
    const signProvider = getSignProviderForDocuments(participantDocument);

    personButtons = (
      <>
        {
          participantDocument.signProcessId ?
            <RejectButton signProvider={signProvider} participantId={participantDocument.participantId}/> : void 0
        }
        <IndentSpace size={10}/>
        {
          participantDocument.signProcessId && !hasAllDocsSigned ?
            <ButtonBase variant="contained" size="small"
                        onClick={() => checkStatus(participantDocument.participantId, signProvider)}
                        disabled={!!showProgress}>Обновить статус</ButtonBase> : void 0
        }
        {!participantDocument.signProcessId ?
          <SignProviderButton
            showProgress={showProgress}
            disabled={disableForSign}
            title="Подписать физлицом"
            sx={{width: 225}}
            onclick={
              (provider) => {
                sendToSign(participantDocument.participantId, SubjectType.PERSON, provider)
              }
            }
          />
          : null}
      </>
    )
  }

  return (
    <>
      {personButtons}
      {participantDocument.externalSignLink ? orgButtons : void 0}
      {
        disableForSign ||
        hasStatus('dealSentToRR') ||
        participantDocument.signProcessId ||
        participantDocument.externalSignLink
          ? null :
          <>
            <IndentSpace size={10}/>
            <ButtonBase variant="contained" size="small"
                        onClick={() => sendToOrgSign(participantDocument.participantId, participantDocument.type)}
                        disabled={!!showProgress}>Создать ссылку для Юрлица</ButtonBase></>}
    </>
  )
}

const CopyButton = ({signlink}) => {
  const {
    openNotify,
    showProgress,
  } = useContext(SignPageContext);

  const copyOnClick = () => {
    navigator.clipboard.writeText(signlink)

    openNotify({
      msg: "Скопировано",
      duration: 1200,
      type: 'success',
      positionH: 'right',
    })
  }

  return (<ButtonBase variant="contained" size="small" disabled={!!showProgress} onClick={copyOnClick}>Скопировать
    ссылку</ButtonBase>)
}

const RejectExtSignButton = ({participantId}) => {
  const {
    showProgress,
    setShowProgressState,
    getListParticipantsWDocs,
    openNotify
  } = useContext(SignPageContext);


  const rejectOrgSign = async () => {
    setShowProgressState(true)
    const result = await sendRejectExtSign(urlQueryDealId, participantId)

    if (result.ok) {
      await getListParticipantsWDocs();
      openNotify({msg: "Документы отозваны с подписания", duration: 3000})
    } else {
      const msg = exceptionsTitle[result.message] || "Не удалось отозвать документы с подписания";
      openNotify({msg, duration: 12000, errors: result.errors, type: 'error'})
    }
    setShowProgressState(false);
  }

  return (<ButtonBase variant="contained" size="small"
                      onClick={rejectOrgSign}
                      disabled={!!showProgress} color="error">Отозвать</ButtonBase>)
}

const RejectButton = ({participantId, signProvider}) => {
  const {
    showProgress,
    rejectDocs
  } = useContext(SignPageContext);

  return (<ButtonBase variant="contained" size="small"
                      onClick={() => rejectDocs(participantId, signProvider)}
                      disabled={!!showProgress} color="error">Отозвать</ButtonBase>)
}